/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useRouterStore, RouterStore } from 'mobx-state-router';
import React, { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

import { Spacing } from 'src/common/ui/base';
import {
  Alert,
  CircleButton,
  Header,
  LoadingSpinner,
  Page,
  PageTitle,
  SmartTable,
} from 'src/common/ui/components';
import { ENROLLMENT_STATUS_OPTIONS } from 'src/components/forms/schemas/definitions';
import RouterLink from 'src/components/general/routerLink';
import { CareTeam } from 'src/components/pages/patients/Patients.CareTeam';
import { FilterEnrollmentStatus } from 'src/components/pages/patients/Patients.Filter.EnrollmentStatus';
import { FilterProvider } from 'src/components/pages/patients/Patients.Filter.Provider';
import { FyiIcon } from 'src/components/pages/patients/Patients.FyiIcon';
import { VisitHistoryDetails } from 'src/components/pages/patients/Patients.VisitHistoryDetails';
import { PatientsQuery } from 'src/generated/gql/graphql';
import { LOAD_PATIENTS } from 'src/stores/queries/patients';

const usePatients = ({
  caseManager,
  enrollmentStatus,
  first,
  orderBy,
  peer,
  prescriber,
  skip,
}: {
  caseManager?: string;
  enrollmentStatus?: string;
  first: number;
  orderBy?: string;
  peer?: string;
  prescriber?: string;
  skip: number;
}) =>
  useSWR<PatientsQuery>([
    LOAD_PATIENTS,
    {
      first,
      orderBy,
      skip,
      where: {
        caseManager_some: caseManager || undefined,
        enrollmentStatus_status_some: enrollmentStatus || undefined,
        peer_some: peer || undefined,
        prescriber_some: prescriber || undefined,
      },
    },
  ]);

const structureItem = (
  item: NonNullable<PatientsQuery['items'][number]>,
  routerStore: RouterStore,
) => ({
  id: item.id as string,
  firstName: item.preferredName ? `${item.preferredName} (${item.firstName})` : item.firstName,
  lastName: item.lastName,
  homeAddress_state: `${item.homeAddress?.state || ''}${
    item.homeAddress?.city ? ` (${item.homeAddress?.city})` : ''
  }`,
  fyi: <FyiIcon fyi={item.fyi} />,
  enrollmentStatus:
    ENROLLMENT_STATUS_OPTIONS[item.enrollmentStatus?.status] || item.enrollmentStatus?.status || '',
  careTeam: <CareTeam item={item} />,
  lastVisit: <VisitHistoryDetails type="latestCompletedVisit" item={item} />,
  lastMissedVisit: <VisitHistoryDetails type="lastMissedVisit" item={item} />,
  nextVisit: <VisitHistoryDetails type="nextVisit" item={item} />,
  onClick: () =>
    routerStore.goTo('showPatient', {
      params: { id: item.id },
    }),
});

const Patients = () => {
  const routerStore = useRouterStore();

  // Manage pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const skip = (currentPage - 1) * rowsPerPage;

  // Manage sorting
  const [sortKey, setSortKey] = useState<string>('lastName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const orderBy = `${sortKey}_${sortDirection.toUpperCase()}`;

  // Filter: Enrollment status
  const [enrollmentStatus, setEnrollmentStatus] = useState<string | null>(
    routerStore.routerState.queryParams.enrollmentStatus,
  );

  // Filter: Provider
  const [selectedPrescriber, setSelectedPrescriber] = useState<string | null | undefined>(
    undefined,
  );
  const [selectedPeer, setSelectedPeer] = useState<string | null | undefined>(undefined);
  const [selectedCaseManager, setSelectedCaseManager] = useState<string | null | undefined>(
    undefined,
  );

  // Fetch patients data results
  const { data, error, isLoading } = usePatients({
    first: rowsPerPage,
    skip,
    orderBy,
    enrollmentStatus: enrollmentStatus || undefined,
    prescriber: selectedPrescriber || undefined,
    peer: selectedPeer || undefined,
    caseManager: selectedCaseManager || undefined,
  });
  const parsedData = useMemo(
    () =>
      data?.items
        ?.map(item => (item ? structureItem(item, routerStore) : null))
        ?.filter((item): item is NonNullable<typeof item> => Boolean(item)) || [],
    [data?.items, routerStore],
  );

  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setCurrentPage(1); // Reset to the first page when rows per page changes
  };

  const handleSortChange = (key: string, direction: 'asc' | 'desc') => {
    setSortKey(key);
    setSortDirection(direction);
  };

  useEffect(() => {
    routerStore.setRouterState({
      ...routerStore.routerState,
      queryParams: {
        ...routerStore.routerState.queryParams,
        first: rowsPerPage,
        skip,
        orderBy,
      },
    });
  }, [rowsPerPage, skip, orderBy, routerStore]);

  return (
    <Page fullWidth>
      <PageTitle
        css={css`
          display: flex;
          justify-content: space-between;
        `}
      >
        <span>Patients</span>
        <RouterLink routeName="createPatient">
          <CircleButton icon="add" color="inverted" />
        </RouterLink>
      </PageTitle>

      <nav
        css={css`
          margin-bottom: ${Spacing.Unit4}px;
        `}
      >
        <Header variant="subtitle1">Filters</Header>
        <div
          css={css`
            display: flex;
            gap: ${Spacing.Unit3}px;
            flex-direction: row;
            flex-wrap: wrap;

            > * {
              flex: 1 1 150px;
              max-width: 350px;
            }
          `}
        >
          <FilterProvider
            label="Clinician"
            onChange={option => setSelectedPrescriber(option?.value || null)}
            teamRole="prescriber"
          />
          <FilterProvider
            label="Peer Coach"
            onChange={option => setSelectedPeer(option?.value || null)}
            teamRole="peer"
          />
          <FilterProvider
            label="Case Manager"
            onChange={option => setSelectedCaseManager(option?.value || null)}
            teamRole="caseManager"
          />
          <FilterEnrollmentStatus onChange={option => setEnrollmentStatus(option?.value || null)} />
        </div>
      </nav>

      <section>
        <Header variant="subtitle1">Results</Header>

        {error ? (
          <Alert title="Issue loading patients" message={error?.message || ''} error={error} />
        ) : null}

        {isLoading ? (
          <figure
            css={css`
              display: block;
              margin: 0;
              padding: 0;
              position: relative;
            `}
          >
            <LoadingSpinner
              css={css`
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
              `}
            />
          </figure>
        ) : null}

        <SmartTable
          css={css`
            background: transparent;
            opacity: ${isLoading ? 0.25 : 1};
          `}
          controlled
          data={parsedData}
          columns={[
            {
              label: 'First',
              key: 'firstName',
              sortable: false,
            },
            {
              label: 'Last',
              key: 'lastName',
              sortable: true,
            },
            {
              label: 'State',
              key: 'homeAddress_state',
              sortable: true,
            },
            {
              label: 'FYI',
              key: 'fyi',
              sortable: false,
            },
            {
              label: 'Status',
              key: 'enrollmentStatus',
              sortable: false,
            },
            {
              label: 'Care Team',
              key: 'careTeam',
              sortable: false,
            },
            {
              label: 'Last Visit',
              key: 'lastVisit',
              sortable: false,
            },
            {
              label: 'Last Missed Visit',
              key: 'lastMissedVisit',
              sortable: false,
            },
            {
              label: 'Next Scheduled Visit',
              key: 'nextVisit',
              sortable: false,
            },
          ]}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 20, 50]}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSortChange={handleSortChange}
          // Unfortunately, the API doesn't return the total number of rows
          totalRows={null}
        />
      </section>
    </Page>
  );
};

export default Patients;
